// CAMPO DE QUANTIDADE COM VÁRIOS NÚMEROS — o "Quartos" da barra dos heros novos (Aurora/Horizonte).
//
// Daniel 20/10: a home dos modelos novos também marca VÁRIOS números (2 e 3 quartos), como a
// lateral de /imoveis (utils/qtdMultiSelecao). Mesmo visual do CampoSelecao vizinho — as classes
// do SmartLocationSearch: texto transparente com a seta, painel em PORTAL no <body> (o porquê
// está lá) com a classe de tema do hero —, mas a lista é de MARCAR: clicar liga/desliga o número
// e a lista continua aberta; "Tanto faz" limpa e fecha.
//
// O valor é o texto "2,3" que o useHeroSearch leva à URL (`dormitorios=2,3`); um número só = "2",
// o valor de sempre. Teclado: ↑ ↓ andam, Enter/Espaço marcam, Esc fecha (Enter com a lista
// fechada segue submetendo o form).
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { alternarQtdTexto, QTD_NUMEROS, qtdListaDoTexto, rotuloQtdTexto } from '../../../utils/qtdMultiSelecao';
import styles from './SmartLocationSearch.module.css';
import local from './CampoQuantidadeMulti.module.css';

export interface CampoQuantidadeMultiProps {
    /** texto "2,3" ('' = nenhum). */
    valor: string;
    onChange: (_valor: string) => void;
    /** a opção que limpa ("Tanto faz") — também o texto do campo vazio. */
    rotuloVazio: string;
    singular: string;
    plural: string;
    ariaLabel: string;
    className?: string;
}

const CampoQuantidadeMulti: React.FC<CampoQuantidadeMultiProps> = ({ valor, onChange, rotuloVazio, singular, plural, ariaLabel, className }) => {
    const reactId = useId();
    const idBase = `qtd-multi-${String(reactId).replace(/[^a-zA-Z0-9]/g, '')}`;
    const [aberto, setAberto] = useState(false);
    // -1 = "Tanto faz"; 0..4 = os números
    const [destaque, setDestaque] = useState(0);
    const [abreParaCima, setAbreParaCima] = useState(false);
    const [caixa, setCaixa] = useState<{ left: number; width: number; top: number; bottom: number } | null>(null);
    const [montado, setMontado] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const painelRef = useRef<HTMLDivElement>(null);
    useEffect(() => setMontado(true), []);

    const marcados = qtdListaDoTexto(valor);
    const texto = rotuloQtdTexto(valor, singular, plural);

    // mesma conta do SmartLocationSearch: onde o painel (fixed, no <body>) aparece e se abre para cima
    useEffect(() => {
        if (!aberto || typeof window === 'undefined') return undefined;
        const medir = () => {
            const r = wrapRef.current?.getBoundingClientRect();
            if (!r) return;
            setCaixa({ left: r.left, width: r.width, top: r.top, bottom: r.bottom });
            const abaixo = window.innerHeight - r.bottom;
            setAbreParaCima(abaixo < 320 && r.top > abaixo);
        };
        medir();
        window.addEventListener('scroll', medir, true);
        window.addEventListener('resize', medir);
        return () => {
            window.removeEventListener('scroll', medir, true);
            window.removeEventListener('resize', medir);
        };
    }, [aberto]);

    useEffect(() => {
        if (!aberto) return undefined;
        const aoClicarFora = (evento: MouseEvent) => {
            const alvo = evento.target as Node;
            if (wrapRef.current?.contains(alvo) || painelRef.current?.contains(alvo)) return;
            setAberto(false);
        };
        document.addEventListener('mousedown', aoClicarFora);
        return () => document.removeEventListener('mousedown', aoClicarFora);
    }, [aberto]);

    const abrir = () => {
        if (aberto) return;
        setDestaque(marcados.length ? QTD_NUMEROS.indexOf(marcados[0]) : 0);
        setAberto(true);
    };
    const limpar = () => {
        onChange('');
        setAberto(false);
    };
    const alternar = (numero: number) => onChange(alternarQtdTexto(valor, numero));

    const aoTeclar = (evento: React.KeyboardEvent<HTMLInputElement>) => {
        if (evento.key === 'Escape') {
            setAberto(false);
            return;
        }
        if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
            evento.preventDefault();
            if (!aberto) {
                abrir();
                return;
            }
            const passo = evento.key === 'ArrowDown' ? 1 : -1;
            setDestaque((atual) => {
                const proximo = atual + passo;
                if (proximo < -1) return QTD_NUMEROS.length - 1;
                if (proximo >= QTD_NUMEROS.length) return -1;
                return proximo;
            });
            return;
        }
        if ((evento.key === 'Enter' || evento.key === ' ') && aberto) {
            evento.preventDefault();
            if (destaque < 0) limpar();
            else alternar(QTD_NUMEROS[destaque]);
            return;
        }
        if (evento.key === ' ') {
            evento.preventDefault();
            abrir();
        }
    };

    const rotuloNumero = (n: number) => `${n === 5 ? '5+' : n} ${n === 1 ? singular : plural}`;

    const conteudoPainel = (
        <ul className={styles.lista} id={`${idBase}-lista`} role="listbox" aria-label={ariaLabel} aria-multiselectable="true">
            <li
                id={`${idBase}-opt--1`}
                className={`${styles.opcao} ${styles.opcaoVazia} ${destaque === -1 ? styles.opcaoAtiva : ''}`}
                role="option"
                aria-selected={!marcados.length}
                onMouseDown={(e) => {
                    e.preventDefault();
                    limpar();
                }}
                onMouseEnter={() => setDestaque(-1)}
            >
                <span className={styles.opcaoTexto}>{rotuloVazio}</span>
            </li>
            {QTD_NUMEROS.map((n, i) => {
                const marcado = marcados.includes(n);
                return (
                    <li
                        key={n}
                        id={`${idBase}-opt-${i}`}
                        role="option"
                        aria-selected={marcado}
                        className={`${styles.opcao} ${local.opcao} ${i === destaque ? styles.opcaoAtiva : ''}`}
                        // mousedown (não click): o input não perde o foco e a lista fica aberta
                        onMouseDown={(e) => {
                            e.preventDefault();
                            setDestaque(i);
                            alternar(n);
                        }}
                        onMouseEnter={() => setDestaque(i)}
                    >
                        <span className={`${local.caixinha} ${marcado ? local.caixinhaMarcada : ''}`} aria-hidden="true" />
                        <span className={styles.opcaoTexto}>
                            <strong className={styles.opcaoLabel}>{rotuloNumero(n)}</strong>
                        </span>
                    </li>
                );
            })}
            <li className={local.dica} role="presentation">
                Marque um ou mais
            </li>
        </ul>
    );

    return (
        <div ref={wrapRef} className={`${styles.wrap} ${className || ''}`}>
            <input
                id={idBase}
                className={styles.input}
                type="text"
                readOnly
                inputMode="none"
                role="combobox"
                autoComplete="off"
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={aberto}
                aria-controls={`${idBase}-lista`}
                aria-activedescendant={aberto ? `${idBase}-opt-${destaque}` : undefined}
                placeholder={rotuloVazio}
                value={texto}
                title={texto || undefined}
                onFocus={abrir}
                onClick={abrir}
                onBlur={() => setAberto(false)}
                onKeyDown={aoTeclar}
            />
            <span className={styles.seta} aria-hidden="true" />
            {montado && aberto && caixa
                ? createPortal(
                      <div
                          ref={painelRef}
                          className={`${styles.painel} ${styles.painelFlutuante} ${abreParaCima ? styles.painelAcima : ''} ${className || ''}`}
                          style={{
                              left: caixa.left - 14,
                              width: Math.max(caixa.width + 28, 200),
                              ...(abreParaCima ? { bottom: window.innerHeight - caixa.top + 10 } : { top: caixa.bottom + 10 }),
                          }}
                      >
                          {conteudoPainel}
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};

export default CampoQuantidadeMulti;
